import { useExplorerStore } from "@/modules/explorer/store";
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import { toRef, watch } from "vue";

type DocumentRule<T> = {
	name: string;
	field: string[];
	handler: (value: T, oldValue: T, fieldName: string) => void;
};

type RelatedDimensionConfiguration<T> = {
	groups: Record<
		string,
		{
			rules: DocumentRule<T>[];
			isUpdating?: string;
		}
	>;
};

/**
 * @param field string notation of an item in the explorerStore (e.g. document.horizontal_dimension).
 * @returns A reactive reference to the item specified in field.
 */
function fieldToRef(field: string) {
	const parts = field.split(".");
	const lastPart = parts.pop();

	let item = useExplorerStore();
	for (const part of parts) {
		item = item[part];
	}

	if (!item) {
		return null;
	}

	return toRef(item, lastPart as keyof typeof item);
}

function unDot(obj: object, field: string): any {
	const parts = field.split(".");

	let item = obj;
	for (const part of parts) {
		if (item === null || !(part in item)) {
			return null;
		}

		item = item[part];
	}

	return item;
}

const isDevelopmentEnv = import.meta.env.DEV;

export default function (setLastUpdated) {
	const explorerStore = useExplorerStore();

	const configuration: RelatedDimensionConfiguration<any> = {
		groups: {
			"date comparison": {
				rules: [
					{
						name: "Set legend/offset to date_comparison when period is selected",
						field: ["document.date_comparison"],
						handler(value, oldValue, fieldName) {
							const oldHadValues =
								oldValue.periods.length > 0 ||
								oldValue.customPeriods.length > 0 ||
								oldValue.isBudgetComparisonActive;
							const newHasValues =
								value.periods.length > 0 ||
								value.customPeriods.length > 0 ||
								value.isBudgetComparisonActive;

							if (newHasValues && !oldHadValues) {
								explorerStore.$patch((state) => {
									const dateDimension = usePropertyStore().dimensions.find(
										(dimension) => dimension.name_clean === "date_comparison"
									);

									// Set offset/legend
									if (state.document.legend_dimension === null) {
										// Only update legend when it's not set.
										state.document.legend_dimension = dateDimension;
									}

									state.document.offset_dimension = dateDimension;
								});
							}
							if (!newHasValues && oldHadValues) {
								explorerStore.$patch((state) => {
									if (
										state.document.legend_dimension?.name_clean ===
										"date_comparison"
									) {
										state.document.legend_dimension = null;
									}
									if (
										state.document.offset_dimension?.name_clean ===
										"date_comparison"
									) {
										state.document.offset_dimension = null;
									}
									state.document.date_comparison.showDeltaAs = "disabled";
								});
							}

							// Enable date comparison when show delta is enabled.
							if (
								!newHasValues &&
								!oldHadValues &&
								value.showDeltaAs !== "disabled"
							) {
								explorerStore.$patch((state) => {
									state.document.date_comparison.periods = [-1];
									// Set offset/legend
									state.document.legend_dimension =
										usePropertyStore().dimensions.find(
											(dimension) => dimension.name_clean === "date_comparison"
										);
									state.document.offset_dimension =
										explorerStore.document.legend_dimension;
								});
							}

							setLastUpdated();
						},
					} as DocumentRule<typeof explorerStore.document.date_comparison>,
					{
						name: "Set periods when legend/offset changes to date_comparison",
						field: ["document.legend_dimension"],
						handler(value, oldValue) {
							const oldHadDateComparison =
								oldValue?.name_clean === "date_comparison";
							const newHasDateComparison =
								explorerStore.document.horizontal_dimension?.name_clean ===
									"date_comparison" ||
								explorerStore.document.legend_dimension?.name_clean ===
									"date_comparison" ||
								explorerStore.document.offset_dimension?.name_clean ===
									"date_comparison";

							if (
								!oldHadDateComparison &&
								newHasDateComparison &&
								explorerStore.document.date_comparison.periods.length === 0 &&
								!explorerStore.document.date_comparison.isBudgetComparisonActive
							) {
								explorerStore.document.date_comparison.periods = [-1];
							}
							if (!newHasDateComparison && oldHadDateComparison) {
								explorerStore.document.date_comparison.periods = [];
								explorerStore.document.date_comparison.customPeriods = [];
								explorerStore.document.date_comparison.isBudgetComparisonActive =
									false;
								explorerStore.document.date_comparison.showDeltaAs = "disabled";
							}
						},
					} as DocumentRule<typeof explorerStore.document.legend_dimension>,
				],
			},

			"budget comparison": {
				rules: [
					{
						name: "Set vertical to revenuesdriveout when budget was selected while current vertical does not support it",
						field: ["document.date_comparison"],
						handler(value, oldValue, fieldName) {
							if (
								!explorerStore.derivedDocument?.date_comparison
									.isBudgetComparisonActive &&
								value.isBudgetComparisonActive &&
								!explorerStore.verticalSupportsBudget
							) {
								explorerStore.$patch((state) => {
									// Inefficient but the order is deterministic.
									state.document.vertical =
										usePropertyStore().verticals.find(
											(v) => v.name_clean === "revenuesdriveout"
										) ||
										usePropertyStore().verticals.find(
											(v) => v.name_clean === "advertising_revenue_distr"
										);
								});

								setLastUpdated();
							}
						},
					} as DocumentRule<typeof explorerStore.document.date_comparison>,
				],
			},

			//todo discuss with julius how to update this piece of code.

			// "updated in background": {
			// 	rules: [
			// 		// When is_updated_in_background is enabled, we want to always use the latest measure date. Otherwise, it's pointless.
			// 		{
			// 			name: "Set is_measure_datetime_latest when is_updated_in_background is enabled",
			// 			field: ["document.is_updated_in_background"],
			// 			handler(value) {
			// 				if (!value) {
			// 					return;
			// 				}
			//
			// 				explorerStore.document.is_measure_datetime_latest =
			// 					value as boolean;
			// 			},
			// 		} as DocumentRule<
			// 			typeof explorerStore.document.is_updated_in_background
			// 		>,
			// 		// When the user does not use the latest measure date, and the document is updated in real time
			// 		// disable the real time functionality. When the measure date stays the same, the data will stay the same.
			// 		{
			// 			name: "Disable is_updated_in_background when is_measure_datetime_latest is disabled",
			// 			field: ["document.is_measure_datetime_latest"],
			// 			handler(value) {
			// 				if (value || !explorerStore.document.is_updated_in_background) {
			// 					return;
			// 				}
			//
			// 				explorerStore.document.is_updated_in_background = false;
			// 			},
			// 		} as DocumentRule<
			// 			typeof explorerStore.document.is_measure_datetime_latest
			// 		>,
			// 	],
			// },

			"legend/offset dimension": {
				rules: [
					{
						name: "When offset changes and legend is null, set legend to offset",
						field: ["document.offset_dimension"],
						handler(value, oldValue) {
							if (
								oldValue !== null ||
								explorerStore.document.legend_dimension !== null
							) {
								return;
							}

							explorerStore.document.legend_dimension =
								explorerStore.document.offset_dimension;
						},
					} as DocumentRule<typeof explorerStore.document.offset_dimension>,
				],
			},
		},
	};

	for (const groupName in configuration.groups) {
		const group = configuration.groups[groupName];
		group.isUpdating = null;

		group.rules.forEach((rule) => {
			rule.field.forEach((fieldRef) => {
				watch(
					() => unDot(explorerStore, fieldRef),
					async (value, oldValue) => {
						if (
							!explorerStore.document ||
							explorerStore.explorerState.isOpeningSavedDocument
						) {
							return;
						}

						if (group.isUpdating !== rule.name && group.isUpdating !== null) {
							isDevelopmentEnv &&
								console.log(
									`[ConfigureRelatedDimension] [${groupName}] Skip ${rule.name} because already updating`
								);
							return;
						}

						group.isUpdating = rule.name;
						isDevelopmentEnv &&
							console.log(
								`[ConfigureRelatedDimension] [${groupName}] Update ${rule.name}`
							);
						await rule.handler(value, oldValue, fieldRef);
						group.isUpdating = null;
					},
					{ flush: "sync", deep: true }
				);
			});
		});
	}
}
